import { useTranslation } from 'react-i18next';
import { CheckCircle2, Download, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import type { EntityCounts, UserCredential } from '../../types';
import { exportCredentialsAsCsv } from '../../api';

interface SuccessResultProps {
  counts: EntityCounts;
  credentials?: UserCredential[];
  onNewOnboarding: () => void;
  dryRun?: boolean;
}

export function SuccessResult({
  counts,
  credentials,
  onNewOnboarding,
  dryRun,
}: SuccessResultProps) {
  const { t } = useTranslation('customer-onboarding');

  const handleExportCredentials = () => {
    if (credentials && credentials.length > 0) {
      exportCredentialsAsCsv(credentials);
      toast.success(t('toast.credentialsExported'));
    }
  };

  const entityEntries = Object.entries(counts).filter(([, count]) => count > 0);

  return (
    <div className={`rounded-lg border p-6 ${dryRun ? 'border-blue-200 bg-blue-50' : 'border-green-200 bg-green-50'}`}>
      <div className="mb-6 flex items-center gap-3">
        <CheckCircle2 className={`h-8 w-8 ${dryRun ? 'text-blue-600' : 'text-green-600'}`} />
        <div>
          <h3 className={`text-lg font-semibold ${dryRun ? 'text-blue-800' : 'text-green-800'}`}>
            {dryRun ? t('result.dryRun.title') : t('result.success.title')}
          </h3>
          <p className={`text-sm ${dryRun ? 'text-blue-700' : 'text-green-700'}`}>
            {dryRun ? t('result.dryRun.description') : t('result.success.description')}
          </p>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
        {entityEntries.map(([entity, count]) => (
          <div
            key={entity}
            className="rounded-lg bg-white p-3 shadow-sm"
          >
            <div className="text-2xl font-bold text-green-700">{count}</div>
            <div className="text-sm text-muted-foreground">
              {t(`execution.steps.${entity}`)}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        <Button onClick={onNewOnboarding} variant="outline">
          <Plus className="mr-2 h-4 w-4" />
          {t('result.success.newOnboarding')}
        </Button>

        {!dryRun && credentials && credentials.length > 0 && (
          <Button onClick={handleExportCredentials}>
            <Download className="mr-2 h-4 w-4" />
            {t('result.success.exportCredentials', { count: credentials.length })}
          </Button>
        )}
      </div>
    </div>
  );
}
